import { defaultBrandConfig } from "./brand.default";
import { BrandConfigSchema, type BrandConfig, type LegalDocumentType } from "./brand.schema";

/**
 * White-label config module — the single typed source every surface reads
 * brand/legal/attribution values from.
 *
 * Values come from `brand.default.ts`, optionally overridden per-deployment
 * via env vars (for white-labeling without a code change/rebuild). This
 * same function works at both build time (e.g. `generateMetadata`, static
 * params) and request time (Route Handlers, Server Components, middleware)
 * — Node process env doesn't change mid-process, so one memoized read
 * serves both.
 */

interface BrandEnvOverrides {
  BRAND_NAME?: string;
  BRAND_LOGO_URL?: string;
  BRAND_COLOR_PRIMARY?: string;
  BRAND_COLOR_ACCENT?: string;
  BRAND_APP_DOMAIN?: string;
  BRAND_SUPPORT_EMAIL?: string;
  BRAND_LEGAL_ENTITY?: string;
  BRAND_LEGAL_URL_TOS?: string;
  BRAND_LEGAL_URL_PRIVACY?: string;
  BRAND_LEGAL_URL_COOKIES?: string;
  BRAND_LEGAL_URL_DPA?: string;
  BRAND_LEGAL_URL_ACCEPTABLE_USE?: string;
  BRAND_ATTRIBUTION_BASE?: string;
  BRAND_ATTRIBUTION_OSM?: string;
  BRAND_ATTRIBUTION_PROTOMAPS?: string;
  BRAND_ATTRIBUTION_MAPTILER?: string;
  BRAND_ATTRIBUTION_STADIA?: string;
  BRAND_ATTRIBUTION_GOOGLE?: string;
  BRAND_ATTRIBUTION_ESRI?: string;
  BRAND_LEGAL_ADDRESS?: string;
  BRAND_LEGAL_JURISDICTION?: string;
  BRAND_LEGAL_GOVERNING_LAW?: string;
  BRAND_LEGAL_PRIVACY_CONTACT?: string;
  BRAND_LEGAL_BADGE_LABEL?: string;
  // M8 per-tool build-go flags — "true"/"1" turns a tool on at deploy time.
  BRAND_ANALYSIS_RADIUS?: string;
  BRAND_ANALYSIS_TERRITORIES_LASSO?: string;
  BRAND_ANALYSIS_TERRITORIES_AUTOMATED?: string;
  BRAND_ANALYSIS_TERRITORIES_BOUNDARY?: string;
  BRAND_ANALYSIS_DRIVE_TIME?: string;
  BRAND_ANALYSIS_ROUTES?: string;
  // M9 demographics/census overlay build-go flag — "true"/"1" turns it on.
  BRAND_DEMOGRAPHICS_OVERLAYS?: string;
  // M10 per-connector OAuth client credentials — supplied ONLY via env at
  // deploy time; never hardcoded. Unset leaves the connector unconfigured (null).
  OAUTH_GSHEET_CLIENT_ID?: string;
  OAUTH_GSHEET_CLIENT_SECRET?: string;
  OAUTH_SALESFORCE_CLIENT_ID?: string;
  OAUTH_SALESFORCE_CLIENT_SECRET?: string;
  OAUTH_HUBSPOT_CLIENT_ID?: string;
  OAUTH_HUBSPOT_CLIENT_SECRET?: string;
  OAUTH_ZOHO_CLIENT_ID?: string;
  OAUTH_ZOHO_CLIENT_SECRET?: string;
}

/** Only set the optional attribution key when a value is actually present. */
function optionalAttribution(env: string | undefined, base: string | undefined): string | undefined {
  return env ?? base;
}

/** Parse a boolean build-go env flag; unset falls back to the base config value. */
function boolEnv(raw: string | undefined, base: boolean): boolean {
  if (raw === undefined) return base;
  return raw === "true" || raw === "1";
}

/** Pure merge function — exported for unit testing without touching process.env. */
export function applyBrandEnvOverrides(base: BrandConfig, env: BrandEnvOverrides): BrandConfig {
  const attribution: BrandConfig["attribution"] = {
    base: env.BRAND_ATTRIBUTION_BASE ?? base.attribution.base,
    osm: env.BRAND_ATTRIBUTION_OSM ?? base.attribution.osm,
    protomaps: env.BRAND_ATTRIBUTION_PROTOMAPS ?? base.attribution.protomaps,
  };
  const maptiler = optionalAttribution(env.BRAND_ATTRIBUTION_MAPTILER, base.attribution.maptiler);
  const stadia = optionalAttribution(env.BRAND_ATTRIBUTION_STADIA, base.attribution.stadia);
  const google = optionalAttribution(env.BRAND_ATTRIBUTION_GOOGLE, base.attribution.google);
  const esri = optionalAttribution(env.BRAND_ATTRIBUTION_ESRI, base.attribution.esri);
  if (maptiler !== undefined) attribution.maptiler = maptiler;
  if (stadia !== undefined) attribution.stadia = stadia;
  if (google !== undefined) attribution.google = google;
  if (esri !== undefined) attribution.esri = esri;

  return {
    schema_version: base.schema_version,
    brandName: env.BRAND_NAME ?? base.brandName,
    logoUrl: env.BRAND_LOGO_URL ?? base.logoUrl,
    colors: {
      primary: env.BRAND_COLOR_PRIMARY ?? base.colors.primary,
      accent: env.BRAND_COLOR_ACCENT ?? base.colors.accent,
    },
    appDomain: env.BRAND_APP_DOMAIN ?? base.appDomain,
    supportEmail: env.BRAND_SUPPORT_EMAIL ?? base.supportEmail,
    legalEntity: env.BRAND_LEGAL_ENTITY ?? base.legalEntity,
    legalUrls: {
      tos: env.BRAND_LEGAL_URL_TOS ?? base.legalUrls.tos,
      privacy: env.BRAND_LEGAL_URL_PRIVACY ?? base.legalUrls.privacy,
      cookies: env.BRAND_LEGAL_URL_COOKIES ?? base.legalUrls.cookies,
      dpa: env.BRAND_LEGAL_URL_DPA ?? base.legalUrls.dpa,
      acceptableUse: env.BRAND_LEGAL_URL_ACCEPTABLE_USE ?? base.legalUrls.acceptableUse,
    },
    attribution,
    legal: {
      registeredAddress: env.BRAND_LEGAL_ADDRESS ?? base.legal.registeredAddress,
      jurisdiction: env.BRAND_LEGAL_JURISDICTION ?? base.legal.jurisdiction,
      governingLaw: env.BRAND_LEGAL_GOVERNING_LAW ?? base.legal.governingLaw,
      privacyContact: env.BRAND_LEGAL_PRIVACY_CONTACT ?? base.legal.privacyContact,
      badgeLabel: env.BRAND_LEGAL_BADGE_LABEL ?? base.legal.badgeLabel,
      // Per-document effective dates carry through wholesale; a white-labeler
      // that needs different dates edits their theme/default (env-flattening a
      // per-document map would be worse than the swap it saves).
      effectiveDates: { ...base.legal.effectiveDates },
    },
    analysisTools: {
      radius: boolEnv(env.BRAND_ANALYSIS_RADIUS, base.analysisTools.radius),
      territoriesLasso: boolEnv(env.BRAND_ANALYSIS_TERRITORIES_LASSO, base.analysisTools.territoriesLasso),
      territoriesAutomated: boolEnv(env.BRAND_ANALYSIS_TERRITORIES_AUTOMATED, base.analysisTools.territoriesAutomated),
      territoriesBoundary: boolEnv(env.BRAND_ANALYSIS_TERRITORIES_BOUNDARY, base.analysisTools.territoriesBoundary),
      driveTime: boolEnv(env.BRAND_ANALYSIS_DRIVE_TIME, base.analysisTools.driveTime),
      routes: boolEnv(env.BRAND_ANALYSIS_ROUTES, base.analysisTools.routes),
    },
    demographicsOverlays: boolEnv(env.BRAND_DEMOGRAPHICS_OVERLAYS, base.demographicsOverlays),
    oauth: {
      gsheet: {
        clientId: env.OAUTH_GSHEET_CLIENT_ID ?? base.oauth.gsheet.clientId,
        clientSecret: env.OAUTH_GSHEET_CLIENT_SECRET ?? base.oauth.gsheet.clientSecret,
      },
      salesforce: {
        clientId: env.OAUTH_SALESFORCE_CLIENT_ID ?? base.oauth.salesforce.clientId,
        clientSecret: env.OAUTH_SALESFORCE_CLIENT_SECRET ?? base.oauth.salesforce.clientSecret,
      },
      hubspot: {
        clientId: env.OAUTH_HUBSPOT_CLIENT_ID ?? base.oauth.hubspot.clientId,
        clientSecret: env.OAUTH_HUBSPOT_CLIENT_SECRET ?? base.oauth.hubspot.clientSecret,
      },
      zoho: {
        clientId: env.OAUTH_ZOHO_CLIENT_ID ?? base.oauth.zoho.clientId,
        clientSecret: env.OAUTH_ZOHO_CLIENT_SECRET ?? base.oauth.zoho.clientSecret,
      },
    },
    // M13 eval thresholds/rates carry through wholesale — a white-labeler that
    // needs different gate baselines edits their theme/default (env-flattening a
    // deep numeric block would be worse than the swap it saves, exactly like
    // `legal.effectiveDates` above).
    eval: structuredClone(base.eval),
  };
}

let cached: BrandConfig | undefined;

/** Typed runtime + build-time accessor. Validates on first call; memoized after. */
export function getBrandConfig(): BrandConfig {
  if (cached) return cached;
  const merged = applyBrandEnvOverrides(defaultBrandConfig, process.env as BrandEnvOverrides);
  const parsed = BrandConfigSchema.safeParse(merged);
  if (!parsed.success) {
    throw new Error(`Invalid white-label brand config: ${parsed.error.message}`);
  }
  cached = parsed.data;
  return cached;
}

/** Test-only escape hatch to force a re-read after mutating process.env. */
export function __resetBrandConfigCacheForTests(): void {
  cached = undefined;
}

/**
 * `brandLegalConfig` — the flat white-label legal projection (M11 contract).
 * Consumed by M6 (embed/export attribution + badge), M7 (acceptance gate,
 * cancel/support links), and M12 (footer, marketing-consent copy). A pure
 * projection of a resolved {@link BrandConfig}, so callers pass the active
 * theme's brand (`getTheme().brand`) and it re-skins with the theme.
 */
export interface BrandLegalConfig {
  entityName: string;
  registeredAddress: string;
  jurisdiction: string;
  governingLaw: string;
  supportEmail: string;
  privacyContact: string;
  brandName: string;
  appDomain: string;
  badgeLabel: string;
  effectiveDates: Record<LegalDocumentType, string>;
}

/** Project a resolved brand config into the M11 `brandLegalConfig` contract. */
export function toBrandLegalConfig(brand: BrandConfig): BrandLegalConfig {
  return {
    entityName: brand.legalEntity,
    registeredAddress: brand.legal.registeredAddress,
    jurisdiction: brand.legal.jurisdiction,
    governingLaw: brand.legal.governingLaw,
    supportEmail: brand.supportEmail,
    privacyContact: brand.legal.privacyContact,
    brandName: brand.brandName,
    appDomain: brand.appDomain,
    badgeLabel: brand.legal.badgeLabel,
    effectiveDates: { ...brand.legal.effectiveDates },
  };
}

export type { BrandConfig } from "./brand.schema";
